// ---------------------------------------------------------------------------
// Extensão FIÁVEL do modelo, em coordenadas de mundo.
//
// PORQUE É QUE ISTO EXISTE (caso real, exemplo "clinic" do PCERT):
//
//   - `model.box` (a caixa do motor) vem com extremos na ordem de 1e12.
//   - `resolveGeometryBox` (união SEM aparo das caixas por elemento) dá
//     1e8 / 1e9 no mesmo modelo.
//   - E, no entanto, o piso lê-se a 1050,00 m e o corte gera geometria
//     perfeitamente normal.
//
// Ou seja: uma MINORIA das caixas por elemento vem corrompida e
// contamina qualquer união simples. Como as vistas 2D usam a extensão
// do modelo para semear a origem de um corte, para dar altura aos
// cortes desenhados sobre a planta e para enquadrar o "Ver no 3D", uma
// caixa 1e12 estraga tudo isso em silêncio.
//
// APARO ROBUSTO: a MEDIANA dos centros dos elementos não se move por
// causa de uma minoria de outliers, por muito extremos que sejam.
// Mede-se depois o desvio TÍPICO (mediana dos desvios absolutos à
// mediana, por eixo) e descarta-se o que estiver muito para lá disso.
//
//   manter |centro - mediana| <= max(K * mad, PISO)
//
// `K = 20` é generoso (um edifício comprido tem elementos a muitos
// desvios típicos do centro) e `PISO = 50 m` evita que um `mad` nulo
// (todos os elementos com o mesmo centro) rejeite extensão legítima.
// Uma caixa a 1e8 do centro nunca sobrevive; um anexo a 80 m sobrevive.
//
// Puro e testável: recebe caixas, devolve caixa.
// ---------------------------------------------------------------------------

export interface BoxLike {
  min: { x: number; y: number; z: number }
  max: { x: number; y: number; z: number }
}

export interface WorldBox {
  min: [number, number, number]
  max: [number, number, number]
}

/** Múltiplo do desvio típico acima do qual um elemento conta como
 *  corrompido. */
export const ROBUST_BOX_K = 20
/** Distância mínima ao centro sempre aceite, em metros. */
export const ROBUST_BOX_FLOOR_M = 50

function median(sorted: number[]): number {
  const n = sorted.length
  if (n === 0) return 0
  const mid = n >> 1
  return n % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2
}

function isFiniteBox(b: BoxLike | null | undefined): b is BoxLike {
  if (!b || !b.min || !b.max) return false
  return (
    Number.isFinite(b.min.x) &&
    Number.isFinite(b.min.y) &&
    Number.isFinite(b.min.z) &&
    Number.isFinite(b.max.x) &&
    Number.isFinite(b.max.y) &&
    Number.isFinite(b.max.z)
  )
}

/** União das caixas depois de deitar fora os outliers. `null` quando
 *  não sobra nenhuma caixa finita. */
export function robustWorldBox(
  boxes: readonly (BoxLike | null | undefined)[],
  opts?: { k?: number; floorM?: number },
): WorldBox | null {
  const k = opts?.k ?? ROBUST_BOX_K
  const floorM = opts?.floorM ?? ROBUST_BOX_FLOOR_M
  const usable = boxes.filter(isFiniteBox)
  if (usable.length === 0) return null

  const centres = usable.map(
    (b): [number, number, number] => [
      (b.min.x + b.max.x) / 2,
      (b.min.y + b.max.y) / 2,
      (b.min.z + b.max.z) / 2,
    ],
  )
  const med: [number, number, number] = [0, 0, 0]
  const tol: [number, number, number] = [0, 0, 0]
  for (let axis = 0; axis < 3; axis++) {
    const vals = centres.map((c) => c[axis]).sort((a, b) => a - b)
    med[axis] = median(vals)
    const devs = vals.map((v) => Math.abs(v - med[axis])).sort((a, b) => a - b)
    tol[axis] = Math.max(k * median(devs), floorM)
  }

  let min: [number, number, number] | null = null
  let max: [number, number, number] | null = null
  for (let i = 0; i < usable.length; i++) {
    const c = centres[i]
    if (
      Math.abs(c[0] - med[0]) > tol[0] ||
      Math.abs(c[1] - med[1]) > tol[1] ||
      Math.abs(c[2] - med[2]) > tol[2]
    ) {
      continue
    }
    const b = usable[i]
    if (!min || !max) {
      min = [b.min.x, b.min.y, b.min.z]
      max = [b.max.x, b.max.y, b.max.z]
      continue
    }
    min[0] = Math.min(min[0], b.min.x)
    min[1] = Math.min(min[1], b.min.y)
    min[2] = Math.min(min[2], b.min.z)
    max[0] = Math.max(max[0], b.max.x)
    max[1] = Math.max(max[1], b.max.y)
    max[2] = Math.max(max[2], b.max.z)
  }
  if (!min || !max) return null
  return { min, max }
}

export function boxCentre(box: WorldBox): [number, number, number] {
  return [
    (box.min[0] + box.max[0]) / 2,
    (box.min[1] + box.max[1]) / 2,
    (box.min[2] + box.max[2]) / 2,
  ]
}

export interface WorldBoxSource {
  modelId?: string
  getItemsIdsWithGeometry: () => Promise<number[]>
  getBoxes: (ids: number[]) => Promise<BoxLike[]>
  /** O Object3D do modelo. A matriz de mundo entra na chave da cache:
   *  mudar a georreferenciação ou a posição move o modelo, e a caixa
   *  antiga deixava de querer dizer alguma coisa. */
  object?: { matrixWorld?: { elements: ArrayLike<number> } }
}

interface BoxCacheEntry {
  /** A instância do modelo: reabrir um ficheiro com o mesmo nome dá
   *  outra instância com o mesmo modelId. */
  source: object
  /** Assinatura da matriz de mundo no momento do cálculo. */
  placement: string
  box: WorldBox | null
}

const boxCache = new Map<string, BoxCacheEntry>()

function placementSignature(model: WorldBoxSource): string {
  const e = model.object?.matrixWorld?.elements
  return e ? Array.from(e).join(',') : ''
}

export function clearWorldBoxCache(modelId?: string): void {
  if (modelId) boxCache.delete(modelId)
  else boxCache.clear()
}

/** Caixa robusta do modelo. Em cache por modelId (as caixas por
 *  elemento são baratas mas não gratuitas, e nada disto muda entre
 *  vistas). Nunca lança. */
export async function modelWorldBox(
  model: WorldBoxSource,
): Promise<WorldBox | null> {
  const key = model.modelId ?? ''
  const placement = placementSignature(model)
  const cached = key ? boxCache.get(key) : undefined
  if (cached && cached.source === model && cached.placement === placement) {
    return cached.box
  }
  let result: WorldBox | null = null
  try {
    const ids = await model.getItemsIdsWithGeometry()
    if (ids && ids.length > 0) {
      const boxes = await model.getBoxes(ids)
      result = robustWorldBox(boxes ?? [])
    }
  } catch {
    result = null
  }
  if (key) boxCache.set(key, { source: model, placement, box: result })
  return result
}
